import { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../../helper/ProtectedRoute';
import dayjs from 'dayjs';
import { Loader2 } from 'lucide-react';

/* The charge fields arrive as raw doubles - a null where nothing was charged
   and values like 0.15000000000000002 where something was. Money is shown to
   two decimals, weights and factors as the plain number, and an absent value
   as a dash so an empty cell never reads as a zero. */
const dash = (value) => value === null || value === undefined || value === '';

const amount = (value) =>
  dash(value)
    ? '-'
    : Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const plain = (value) => (dash(value) ? '-' : String(Number(value)));

const text = (value) => (dash(value) ? '-' : value);

const TableComponent = ({
  tableData,
  isLoading,
  /* The loading and empty rows span all 36 columns, so inside a horizontally
     scrolled container their text ends up wherever the table happens to be
     panned. A caller that draws its own message outside the scroller turns
     them off and keeps just the column headers. */
  showPlaceholderRow = true,
}) => {
  const [state] = useContext(UserContext);
  const [role, setRole] = useState(sessionStorage.getItem('user_access_role'));
  useEffect(() => {
    setRole(state.roles);
  }, [state.roles]);

  return (
    <>
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">#</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Tracking Id</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Customer Reference No</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Origin Country</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Origin City</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Destination Country</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Destination City</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Billing Country</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Tier</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Product</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Billing Weight</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">COD Currency</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">COD Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">COD Converted Amt</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">COD Conversion Factor</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Freight Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">RTO Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">ODA Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Fuel Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Insurance Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Platform Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Total Charge</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Net Receivable</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">GPA Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">VAT</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">VAT Percentage</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">VAT Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Low Clearance Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">High Clearance Charges</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Clearance Conversion Factor</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Customer Billing Currency</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Conversion Factor</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Billing Currency</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Pickup Date</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Delivery Date</th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {isLoading || !tableData?.length ? (
          !showPlaceholderRow ? null : (
            <tr>
              <td colSpan={36} className="px-6 py-12 text-center text-gray-500">
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : (
                  'No records found'
                )}
              </td>
            </tr>
          )
        ) : (
          tableData.map((item, index) => (
            <tr key={index} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
              <td className="tbl-td">{index + 1}</td>
              <td className="tbl-td">
                <button
                  onClick={() => {
                    sessionStorage.setItem('token', state.token);
                    window.open(`/consignments/orders/${item?.trackingId}`, '_blank');
                  }}
                  className="text-blue-600 hover:text-blue-800 hover:underline bg-transparent"
                >
                  {item?.trackingId}
                </button>
              </td>
              <td className="tbl-td">{text(item?.customerReferenceNumber)}</td>
              <td className="tbl-td">{text(item?.originCountry)}</td>
              <td className="tbl-td">{text(item?.originCity)}</td>
              <td className="tbl-td">{text(item?.destinationCountry)}</td>
              <td className="tbl-td">{text(item?.destinationCity)}</td>
              <td className="tbl-td">{text(item?.billingCountry)}</td>
              <td className="tbl-td">{text(item?.tier)}</td>
              <td className="tbl-td">{text(item?.productCode)}</td>
              <td className="tbl-td">{plain(item?.billingWeight)}</td>
              <td className="tbl-td">{text(item?.codCurrency)}</td>
              <td className="tbl-td">{amount(item?.codCharges)}</td>
              <td className="tbl-td">{amount(item?.codConvertedAmount ?? item?.codAmount)}</td>
              <td className="tbl-td">{plain(item?.codConversionFactor)}</td>
              <td className="tbl-td">{amount(item?.freightCharges)}</td>
              <td className="tbl-td">{amount(item?.rtoCharges)}</td>
              <td className="tbl-td">{amount(item?.odaCharges)}</td>
              <td className="tbl-td">{amount(item?.fuelCharges)}</td>
              <td className="tbl-td">{amount(item?.insuranceCharges)}</td>
              <td className="tbl-td">{amount(item?.platformCharges)}</td>
              <td className="tbl-td">{amount(item?.totalCharge)}</td>
              <td className="tbl-td">{amount(item?.netReceivable)}</td>
              <td className="tbl-td">{amount(item?.gpaCharges)}</td>
              <td className="tbl-td">{amount(item?.vat)}</td>
              <td className="tbl-td">{plain(item?.vatPercentage)}</td>
              <td className="tbl-td">{amount(item?.vatCharges)}</td>
              <td className="tbl-td">{amount(item?.lowClearanceCharges)}</td>
              <td className="tbl-td">{amount(item?.highClearanceCharges)}</td>
              <td className="tbl-td">{plain(item?.clearanceConversionFactor)}</td>
              <td className="tbl-td">{text(item?.customerBillingCurrency)}</td>
              <td className="tbl-td">{plain(item?.conversionFactor)}</td>
              <td className="tbl-td">{text(item?.billingCurrency)}</td>
              <td className="tbl-td">
                {item?.pickupAt ? dayjs(item?.pickupAt).format('DD MMM YYYY, hh:mm:ss A') : '-'}
              </td>
              <td className="tbl-td">
                {item?.deliveredAt ? dayjs(item?.deliveredAt).format('DD MMM YYYY, hh:mm:ss A') : '-'}
              </td>
              <td className="tbl-td">{text(item?.status)}</td>
            </tr>
          ))
        )}
      </tbody>
    </>
  );
};

export default TableComponent;
